"use client"

import { useCallback, useEffect, useState } from "react"
import { AlertTriangle } from "lucide-react"
import { sendCrashReport } from "@/lib/crash-report"

type ReportStatus = "sending" | "sent" | "failed"

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  const [status, setStatus] = useState<ReportStatus>("sending")
  const [reportId, setReportId] = useState<string | null>(null)

  const report = useCallback(async () => {
    setStatus("sending")
    const id = await sendCrashReport("route-error", error)
    if (id) {
      setReportId(id)
      setStatus("sent")
    } else {
      setStatus("failed")
    }
  }, [error])

  useEffect(() => {
    console.error("Route error:", error)
    void report()
  }, [error, report])

  return (
    <main className="mx-auto flex min-h-screen w-full max-w-2xl flex-col items-center justify-center bg-[#FBF8FF] px-6 text-center text-[#1E3144]">
      <div className="mb-5 rounded-full bg-red-100 p-3 text-red-600" aria-hidden>
        <AlertTriangle className="h-7 w-7" />
      </div>
      <h1 className="mb-3 text-2xl font-bold">Wystąpił błąd</h1>
      <p className="mb-6 text-sm text-[#4E5562]">
        Spróbuj ponownie. Jeżeli problem będzie wracał, odśwież stronę.
      </p>

      <div className="mb-8 min-h-10 text-sm text-[#4E5562]" aria-live="polite">
        {status === "sending" && <span>Wysyłamy raport o błędzie…</span>}
        {status === "sent" && reportId && (
          <span>
            Raport wysłany. Numer zgłoszenia:{" "}
            <strong className="font-mono text-[#1E3144]">{reportId}</strong>
          </span>
        )}
        {status === "failed" && (
          <button
            type="button"
            onClick={() => void report()}
            className="rounded-full border border-[rgba(85,54,109,1)] px-5 py-2 text-sm font-semibold text-[rgba(85,54,109,1)] hover:bg-[rgba(85,54,109,0.08)]"
          >
            Wyślij raport o błędzie
          </button>
        )}
      </div>

      <button
        type="button"
        onClick={reset}
        className="rounded-full bg-[rgba(85,54,109,1)] px-6 py-2 text-sm font-semibold text-white hover:bg-[rgba(85,54,109,0.9)]"
      >
        Spróbuj ponownie
      </button>
    </main>
  )
}
